/**
 * Address formatting helpers for the structured registered/operational address.
 *
 * - `composeAddressLines` builds an envelope-style multi-line block from the
 *   structured sub-fields (for read-mode display).
 * - `splitAddress` produces a best-effort starting point for the edit form by
 *   splitting the legacy free-text address. It is a SEED only (operator reviews
 *   and corrects before saving) — never persisted automatically.
 */

export interface AddressParts {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  stateProvince?: string | null;
  postalCode?: string | null;
  countryCode?: string | null;
}

const clean = (v: string | null | undefined): string => (v ?? "").trim();

/**
 * Compose the structured parts into display lines (empties omitted):
 *   line1
 *   line2
 *   "City, State Postal"
 *   "CountryName (CODE)"  (or just "CODE")
 * Returns [] when nothing is set.
 */
export function composeAddressLines(parts: AddressParts, countryName?: string | null): string[] {
  const lines: string[] = [];
  if (clean(parts.line1)) lines.push(clean(parts.line1));
  if (clean(parts.line2)) lines.push(clean(parts.line2));

  // "City, State, Postal" — comma-joined (empties omitted).
  const cityLine = [clean(parts.city), clean(parts.stateProvince), clean(parts.postalCode)]
    .filter(Boolean)
    .join(", ");
  if (cityLine) lines.push(cityLine);

  const code = clean(parts.countryCode);
  if (code) {
    const name = clean(countryName);
    lines.push(name ? `${name} (${code})` : code);
  }
  return lines;
}

/** True when none of the structured parts are set. */
export function isAddressEmpty(parts: AddressParts): boolean {
  return composeAddressLines(parts).length === 0;
}

/**
 * Best-effort seed for the edit form from a free-text address.
 * Country code always comes from the entity's own ISO2 (reliable).
 * The rest is a heuristic comma split the operator confirms.
 */
export function splitAddress(
  freeText: string | null | undefined,
  entityIso2: string | null | undefined,
): Required<{ [K in keyof AddressParts]: string }> {
  const seed = {
    line1: "",
    line2: "",
    city: "",
    stateProvince: "",
    postalCode: "",
    countryCode: clean(entityIso2).toUpperCase(),
  };

  let parts = clean(freeText)
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return seed;

  seed.line1 = parts[0];

  // Drop a trailing part that is a country name (we use the ISO2 code instead).
  // Heuristic: the last part has no digits.
  // Guard `parts.length > 2` (not `> 1`) so a 2-part address like
  // "PO Box 12, Victoria" keeps "Victoria" as the city instead of dropping it.
  const last = parts[parts.length - 1];
  if (parts.length > 2 && !/\d/.test(last)) {
    parts = parts.slice(0, -1);
  }

  // Postal code: first remaining part (after line1) that contains a digit.
  const postalIdx = parts.findIndex((p, i) => i > 0 && /\d/.test(p));
  if (postalIdx !== -1) {
    seed.postalCode = parts[postalIdx];
    // City: the part just before the postal part, if any (and not line1).
    if (postalIdx - 1 >= 1) seed.city = parts[postalIdx - 1];
  } else if (parts.length > 1) {
    // No postal — treat the 2nd part as the city.
    seed.city = parts[1];
  }

  return seed;
}
